import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ClipboardList } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { api } from "../../api";
import { estimateLabel } from "../../estimate";
import { compactTokens, duration, usd } from "../../lib/format";
import type { AgentSummary, RunListItem, Task } from "../../types";
import { ColorDot, StatusBadge, ToneBadge } from "../status-badge";
import { TimeAgo } from "../time-ago";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible";
import { Separator } from "../ui/separator";
import { StationRunDetail } from "./station-detail";

/**
 * One station's history, collapsed by default. `resultText`, `structured` and
 * `handoff` aren't in `run` — the list endpoint leaves them off every row to
 * stay well under the Data API's 1 MB cap — so opening a row fetches this
 * run's full detail and renders the same body as the current station.
 */
export function RunRow({
  run,
  task,
  agent,
  detail: showDetail = true,
  actions = true,
}: {
  run: RunListItem;
  task: Task;
  agent: AgentSummary | undefined;
  /** Fetch the full run for its timeline, prompt and final message. `/runs/:id` is admin only. */
  detail?: boolean;
  actions?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const detail = useQuery({
    queryKey: ["run", run.id],
    queryFn: () => api.run(run.id),
    enabled: open && showDetail,
  });
  const full = detail.data?.run;
  const estimate = estimateLabel(full?.structured);
  const cost = run.usage?.costUsd;
  const tokens = run.usage?.totalTokens;
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="rounded-lg border">
      <CollapsibleTrigger className="flex w-full flex-wrap items-center gap-x-3 gap-y-2 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-muted/50">
        {agent && <ColorDot color={agent.station.color} />}
        <span className="font-medium">{agent?.name ?? run.agentId}</span>
        <StatusBadge status={run.status} />
        {run.attempt > 1 && <ToneBadge tone="amber">Attempt {run.attempt}</ToneBadge>}
        {run.verdict && <ToneBadge tone="violet">{run.verdict}</ToneBadge>}
        {estimate && <ToneBadge tone="sky">{estimate}</ToneBadge>}
        <span className="ml-auto flex items-center gap-2 text-xs text-muted-foreground tabular-nums">
          {run.provider} · <TimeAgo at={run.createdAt} /> · {duration(run.startedAt, run.finishedAt)}
          {cost !== undefined && <span className="text-foreground"> · {usd(cost)}</span>}
          {tokens !== undefined && (
            <span title={`${tokens.toLocaleString()} tokens`}> · {compactTokens(tokens)}</span>
          )}
          <ChevronDown className={cn("size-4 transition-transform", open && "rotate-180")} />
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <Separator />
        <StationRunDetail
          run={full ?? run}
          task={task}
          agent={agent}
          framed="panel"
          timeline={showDetail}
          actions={actions}
        >
          {full?.prompt && <Disclosure label="Prompt" text={full.prompt} />}
          {full?.resultText && <Disclosure label="Final message" text={full.resultText} />}
        </StationRunDetail>
      </CollapsibleContent>
    </Collapsible>
  );
}

function Disclosure({ label, text }: { label: string; text: string }) {
  return (
    <Collapsible className="contents">
      <CollapsibleTrigger className="group inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground">
        <ClipboardList className="size-3.5" />
        {label}
        <ChevronDown className="size-3.5 transition-transform group-data-[state=open]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent className="basis-full">
        <pre className="max-h-48 overflow-auto rounded-md border bg-muted/40 p-2.5 font-mono text-xs leading-relaxed whitespace-pre-wrap">
          {text}
        </pre>
      </CollapsibleContent>
    </Collapsible>
  );
}
